/**
 * ProfilePage renders the former profile tab.
 * The visible tab is "Ссылки": QR codes and client entry links.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { buildMasterLandingUrl, buildVkMiniAppLaunchUrl } from "@zapis24/utils";
import { apiFetch } from "../api.js";

interface Business {
  id: string;
  slug: string;
}

const LANDING_BASE_URL = import.meta.env["VITE_LANDING_URL"] ?? "https://masters.zapis24.online";
const LANDING_LINK_FORMAT = import.meta.env["VITE_LANDING_LINK_FORMAT"] as string | undefined;
const LANDING_USE_SLUG_SUBDOMAINS =
  import.meta.env["VITE_LANDING_USE_SLUG_SUBDOMAINS"] as string | undefined;
const MINI_APP_URL = import.meta.env["VITE_MINI_APP_URL"] ?? "https://mini.zapis24.online";
const VK_APP_ID = import.meta.env["VITE_VK_APP_ID"] as string | undefined;
const TELEGRAM_BOT_URL = getOptionalBotUrl(
  import.meta.env["VITE_TELEGRAM_BOT_URL"] as string | undefined,
  "https://t.me/Zap_is_Pro_Bot"
);
const MAX_BOT_URL = getOptionalBotUrl(
  import.meta.env["VITE_MAX_BOT_URL"] as string | undefined,
  "https://max.ru/id550113317815_bot"
);
const BOT_START_ENV =
  ((import.meta.env["VITE_BOT_START_ENV"] as string | undefined) ?? "")
    .trim()
    .toLowerCase();
const USE_STAGING_BOT_PAYLOAD = BOT_START_ENV === "staging";

function getOptionalBotUrl(envValue: string | undefined, fallback: string): string | null {
  const value = envValue ?? fallback;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const normalized = trimmed.toLowerCase();
  if (
    normalized === "disabled" ||
    normalized.includes("replace-with") ||
    normalized.includes("placeholder")
  ) {
    return null;
  }

  return trimmed;
}

function buildLandingUrl(slug: string): string {
  return buildMasterLandingUrl(slug, {
    baseUrl: LANDING_BASE_URL,
    format: LANDING_LINK_FORMAT,
    useSlugSubdomains: LANDING_USE_SLUG_SUBDOMAINS,
  });
}

function buildUrlWithParams(baseUrl: string, params: Record<string, string>): string {
  try {
    const url = new URL(baseUrl);
    for (const [key, value] of Object.entries(params)) {
      url.searchParams.set(key, value);
    }
    return url.toString();
  } catch {
    const base = baseUrl.replace(/\/$/, "");
    const search = new URLSearchParams(params).toString();
    const separator = base.includes("?") ? "&" : "?";
    return `${base}${separator}${search}`;
  }
}

function buildBotStartPayload(businessId: string): string {
  return USE_STAGING_BOT_PAYLOAD
    ? `staging_business_${businessId}`
    : `business_${businessId}`;
}

function buildBotStartUrl(baseUrl: string, businessId: string): string {
  return buildUrlWithParams(baseUrl, { start: buildBotStartPayload(businessId) });
}

export function ProfilePage({
  token,
  onOpenSite,
}: {
  token: string;
  onOpenSite?: () => void;
}) {
  const { data: business, isLoading } = useQuery({
    queryKey: ["business-me"],
    queryFn: () => apiFetch<Business>(token, "/api/business/me"),
  });

  if (isLoading) {
    return <div className="p-6 text-center text-gray-400 text-sm">Загрузка...</div>;
  }

  const businessId = business?.id ?? "";
  const slug = business?.slug ?? "";
  const landingUrl = buildLandingUrl(slug);
  const tgDeepLink = TELEGRAM_BOT_URL ? buildBotStartUrl(TELEGRAM_BOT_URL, businessId) : null;
  const maxDeepLink = MAX_BOT_URL ? buildBotStartUrl(MAX_BOT_URL, businessId) : null;
  const vkMiniAppLink = buildVkMiniAppLaunchUrl({
    vkAppId: VK_APP_ID ?? null,
    fallbackMiniAppUrl: MINI_APP_URL,
    businessId,
    platform: "vk",
  });

  return (
    <div className="mx-auto w-full max-w-2xl min-w-0 space-y-5 overflow-x-hidden p-4 pb-24">
      <div>
        <h2 className="text-lg font-bold text-gray-800 mb-1">Ссылки и QR-коды</h2>
        <p className="text-sm leading-5 text-gray-500">
          QR-коды и ссылки для клиентов собраны здесь. Эти ссылки можно отправлять клиентам.
        </p>
      </div>

      <div className="min-w-0 rounded-2xl border border-primary/10 bg-primary/5 p-4">
        <p className="text-sm font-semibold text-gray-800">
          Оформление сайта находится в меню во вкладке «Сайт»
        </p>
        <p className="mt-1 text-xs leading-5 text-gray-500">
          Там находятся тексты, логотип, цвет и кнопки.
        </p>
        {onOpenSite && (
          <button
            type="button"
            onClick={onOpenSite}
            className="mt-3 rounded-xl bg-white px-3 py-2 text-xs font-semibold text-primary shadow-sm transition hover:bg-primary/5"
          >
            Открыть сайт
          </button>
        )}
      </div>

      <Section title="QR и ссылки для клиентов">
        <div className="mb-3 rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs leading-5 text-emerald-700">
          <span className="font-semibold">Можно отправлять клиентам.</span>{" "}
          Клиент откроет запись к вам, а не служебные уведомления мастера.
        </div>
        <p className="text-xs leading-5 text-gray-500 mb-3">
          Telegram и MAX работают через общие боты Запись24.
        </p>
        <div className="space-y-3">
          <LinkItem label="Сайт мастера" url={landingUrl} />
          {tgDeepLink && (
            <LinkItem label="Telegram-бот для записи" url={tgDeepLink} />
          )}
          {maxDeepLink && (
            <LinkItem label="Клиентская MAX-ссылка для записи" url={maxDeepLink} />
          )}
          <LinkItem label="ВКонтакте мини-апп" url={vkMiniAppLink} />
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function LinkItem({ label, url }: { label: string; url: string }) {
  const [copied, setCopied] = useState(false);

  function markCopied() {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function copyLink() {
    if (!navigator.clipboard) return;

    void navigator.clipboard.writeText(url).then(markCopied).catch(() => undefined);
  }

  function openLink() {
    window.open(url, "_blank", "noopener,noreferrer");
  }

  function shareLink() {
    if (navigator.share) {
      void navigator.share({ title: label, url }).catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        copyLink();
      });
      return;
    }

    copyLink();
  }

  return (
    <div className="min-w-0 space-y-2 rounded-xl border border-gray-100 p-3">
      <span className="block min-w-0 truncate text-xs font-medium text-gray-700">{label}</span>
      <p className="break-all font-mono text-xs leading-5 text-gray-400">{url}</p>
      <div className="flex items-center justify-center gap-3 pt-1">
        <QRCodeSVG
          value={url}
          size={96}
          level="M"
          marginSize={4}
          title={`QR: ${label}`}
          role="img"
          aria-label={`QR: ${label}`}
          className="h-24 w-24 shrink-0"
        />
        <div className="flex w-28 flex-col gap-1.5">
          <button
            type="button"
            onClick={copyLink}
            aria-label={`Копировать ссылку: ${label}`}
            className="inline-flex h-8 items-center justify-center rounded-lg bg-primary/5 px-2 text-xs font-semibold text-primary transition hover:bg-primary/10 hover:text-primary/80"
          >
            {copied ? "Скопировано ✓" : "Копировать"}
          </button>
          <button
            type="button"
            onClick={openLink}
            aria-label={`Открыть ссылку: ${label}`}
            className="inline-flex h-8 items-center justify-center rounded-lg bg-primary/5 px-2 text-xs font-semibold text-primary transition hover:bg-primary/10 hover:text-primary/80"
          >
            Открыть
          </button>
          <button
            type="button"
            onClick={shareLink}
            aria-label={`Поделиться ссылкой: ${label}`}
            className="inline-flex h-8 items-center justify-center rounded-lg bg-primary/5 px-2 text-xs font-semibold text-primary transition hover:bg-primary/10 hover:text-primary/80"
          >
            Поделиться
          </button>
        </div>
      </div>
    </div>
  );
}
